import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fileUrl } from "@/lib/api";
import { EDITION } from "@/lib/edition";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { toast } from "sonner";
import {
  CalendarStar, MapPin, VideoCamera, Users, Clock, Plus, Globe, Sparkle, Heart, ArrowSquareOut, GlobeHemisphereWest,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { PageIntro } from "@/components/PageIntro";
import SampleLinks from "@/components/SampleLinks";

const EMPTY = {
  title: "", description: "", country: "", event_type: "", event_category: "", event_name: "", custom_name: "",
  institution_id: "", organizer_unit: "", organizer: "", contact_person: "", contact_email: "",
  date: "", end: "", mode: "In-Person", venue: "", campus: "", room: "", online_link: "",
  registration_enabled: true, registration_deadline: "", entry_fee: "", max_participants: 0,
  audience: [], age_group: "", prizes: "", volunteers_needed: 0, cover: "", recurring: false,
  video_urls: "", google_album_url: "",
};

function EventDate({ iso }) {
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return (
    <div className="absolute left-3 top-3 rounded-lg bg-card px-3 py-1.5 text-center shadow">
      <div className="text-[10px] uppercase text-secondary">{d.toLocaleString("en", { month: "short" })}</div>
      <div className="font-heading text-xl font-semibold leading-none">{d.getDate()}</div>
    </div>
  );
}

function EventCard({ e }) {
  const d = new Date(e.date);
  return (
    <Link to={`/events/${e.id}`} className="group overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm transition-colors hover:border-secondary/40" data-testid={`event-card-${e.id}`}>
      <div className="relative h-40 bg-primary">
        {e.cover ? <img src={e.cover.startsWith("http") ? e.cover : fileUrl(e.cover)} alt={e.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          : <div className="grid h-full place-items-center"><CalendarStar size={40} className="text-primary-foreground/60" weight="duotone" /></div>}
        <EventDate iso={e.date} />
      </div>
      <div className="p-5">
        <div className="flex flex-wrap gap-1.5">
          {e.event_type && <Badge variant="secondary" className="rounded-full font-normal">{e.event_type}</Badge>}
          {e.mode && <Badge variant="outline" className="rounded-full font-normal">{e.mode}</Badge>}
        </div>
        <h3 className="mt-2 font-heading text-lg font-medium leading-tight">{e.title}</h3>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Clock size={13} /> {!isNaN(d) ? d.toLocaleString("en", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</span>
          {e.country && <span className="flex items-center gap-1"><Globe size={13} /> {e.country}</span>}
          {(e.venue || e.location) && <span className="flex items-center gap-1"><MapPin size={13} /> {e.venue || e.location}</span>}
          {e.mode === "Online" && <span className="flex items-center gap-1"><VideoCamera size={13} /> Online</span>}
        </div>
        <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Users size={13} /> {e.rsvp_count || 0} going</span>
          <span className="flex items-center gap-1"><Heart size={13} /> {e.interested_count || 0} interested</span>
        </div>
      </div>
    </Link>
  );
}

function CreateEventDialog({ onCreated }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [tax, setTax] = useState({ types: [], modes: ["In-Person", "Online", "Hybrid"], audiences: [] });
  const [f, setF] = useState(EMPTY);
  const [insts, setInsts] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/events/taxonomy", { params: { country: f.country } }).then((r) => setTax(r.data)).catch(() => {});
  }, [open, f.country]);
  useEffect(() => {
    if (!open || !user) return;
    api.get("/institutions/mine").then((r) => setInsts(r.data || [])).catch(() => setInsts([]));
  }, [open, user]);

  const categories = useMemo(() => (tax.types.find((t) => t.type === f.event_type)?.categories) || [], [tax, f.event_type]);
  const names = useMemo(() => (categories.find((c) => c.category === f.event_category)?.names) || [], [categories, f.event_category]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.title || !f.date) return toast.error("Event name and start date are required");
    const payload = {
      title: f.title, description: f.description, country: f.country,
      event_type: f.event_type, event_category: f.event_category,
      event_name: f.event_name === "Other (specify)" ? f.custom_name : f.event_name,
      institution_id: f.institution_id || null, organizer_unit: f.organizer_unit,
      organizer: f.organizer, contact_person: f.contact_person, contact_email: f.contact_email,
      date: new Date(f.date).toISOString(), end: f.end ? new Date(f.end).toISOString() : "",
      mode: f.mode, venue: f.venue, campus: f.campus, room: f.room, online_link: f.online_link,
      registration_enabled: f.registration_enabled, registration_deadline: f.registration_deadline ? new Date(f.registration_deadline).toISOString() : "",
      entry_fee: f.entry_fee, max_participants: Number(f.max_participants) || 0,
      audience: f.audience, age_group: f.age_group, prizes: f.prizes, volunteers_needed: Number(f.volunteers_needed) || 0,
      cover: f.cover, google_album_url: f.google_album_url, recurring: f.recurring,
      video_urls: f.video_urls ? f.video_urls.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean) : [],
    };
    try {
      const r = await api.post("/events", payload);
      toast.success("Event created 🎉");
      setOpen(false); setF(EMPTY);
      onCreated?.(r.data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!user) {
    return <Link to="/register"><Button className="rounded-full gap-1.5" data-testid="events-create-cta"><Plus size={16} /> Host an event</Button></Link>;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="rounded-full gap-1.5" data-testid="events-create-cta"><Plus size={16} /> Host an event</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-2xl font-light">Host an event</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Country</Label>
              <Select value={f.country} onValueChange={(v) => set("country", v)}>
                <SelectTrigger data-testid="ev-country"><SelectValue placeholder="Select country" /></SelectTrigger>
                <SelectContent>{["India", "USA", "UK", "United States", "Australia", "Canada", "Singapore", "Other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {insts.length > 0 && (
              <div className="space-y-1.5"><Label>Institution (optional)</Label>
                <Select value={f.institution_id} onValueChange={(v) => set("institution_id", v)}>
                  <SelectTrigger data-testid="ev-institution"><SelectValue placeholder="Personal / alumni event" /></SelectTrigger>
                  <SelectContent>{insts.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-border/60 bg-muted/30 p-4">
            <p className="mb-2 flex items-center gap-1.5 text-sm font-medium text-primary"><Sparkle size={15} weight="fill" /> Event category</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="space-y-1.5"><Label className="text-xs">Type</Label>
                <Select value={f.event_type} onValueChange={(v) => setF((p) => ({ ...p, event_type: v, event_category: "", event_name: "" }))}>
                  <SelectTrigger data-testid="ev-type"><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent className="max-h-72">{tax.types.map((t) => <SelectItem key={t.type} value={t.type}>{t.type}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Category</Label>
                <Select value={f.event_category} onValueChange={(v) => setF((p) => ({ ...p, event_category: v, event_name: "" }))} disabled={!f.event_type}>
                  <SelectTrigger data-testid="ev-category"><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent className="max-h-72">{categories.map((c) => <SelectItem key={c.category} value={c.category}>{c.category}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Event name</Label>
                <Select value={f.event_name} onValueChange={(v) => setF((p) => ({ ...p, event_name: v, title: v !== "Other (specify)" && !p.title ? v : p.title }))} disabled={!f.event_category}>
                  <SelectTrigger data-testid="ev-name"><SelectValue placeholder="Event name" /></SelectTrigger>
                  <SelectContent className="max-h-72">{names.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {f.event_name === "Other (specify)" && (
              <div className="mt-2 space-y-1.5"><Label className="text-xs">Specify event name</Label><Input value={f.custom_name} onChange={(e) => set("custom_name", e.target.value)} placeholder="Custom event name" data-testid="ev-custom-name" /></div>
            )}
          </div>

          <div className="space-y-1.5"><Label>Event title</Label><Input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. GMEC Annual Youth Festival 2026" data-testid="ev-title" /></div>
          <div className="space-y-1.5"><Label>Description</Label><Textarea rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="What's this event about?" data-testid="ev-desc" /></div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Starts</Label><Input type="datetime-local" value={f.date} onChange={(e) => set("date", e.target.value)} data-testid="ev-date" /></div>
            <div className="space-y-1.5"><Label>Ends (optional)</Label><Input type="datetime-local" value={f.end} onChange={(e) => set("end", e.target.value)} data-testid="ev-end" /></div>
          </div>

          <div className="flex items-center justify-between rounded-xl border border-border/60 p-3">
            <div><Label>Repeats every year</Label><p className="text-xs text-muted-foreground">Annual reunions & sports days auto-roll to next year</p></div>
            <Switch checked={f.recurring} onCheckedChange={(v) => set("recurring", v)} data-testid="ev-recurring-switch" />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Mode</Label>
              <Select value={f.mode} onValueChange={(v) => set("mode", v)}>
                <SelectTrigger data-testid="ev-mode"><SelectValue /></SelectTrigger>
                <SelectContent>{tax.modes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Organizer unit (dept / course / club)</Label><Input value={f.organizer_unit} onChange={(e) => set("organizer_unit", e.target.value)} placeholder="e.g. NSS Unit, CSE Dept" /></div>
          </div>

          {f.mode !== "Online" && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="space-y-1.5"><Label>Venue</Label><Input value={f.venue} onChange={(e) => set("venue", e.target.value)} placeholder="Auditorium" /></div>
              <div className="space-y-1.5"><Label>Campus</Label><Input value={f.campus} onChange={(e) => set("campus", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Room</Label><Input value={f.room} onChange={(e) => set("room", e.target.value)} /></div>
            </div>
          )}
          {f.mode !== "In-Person" && (
            <div className="space-y-1.5"><Label>Online link</Label><Input value={f.online_link} onChange={(e) => set("online_link", e.target.value)} placeholder="https://meet…" /></div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Organizer</Label><Input value={f.organizer} onChange={(e) => set("organizer", e.target.value)} placeholder="Hosting body" /></div>
            <div className="space-y-1.5"><Label>Contact email</Label><Input value={f.contact_email} onChange={(e) => set("contact_email", e.target.value)} placeholder="organizer@…" /></div>
          </div>

          <div className="flex items-center justify-between rounded-xl border border-border/60 p-3">
            <div><Label>Registration open</Label><p className="text-xs text-muted-foreground">Let attendees register / RSVP</p></div>
            <Switch checked={f.registration_enabled} onCheckedChange={(v) => set("registration_enabled", v)} data-testid="ev-reg-switch" />
          </div>
          {f.registration_enabled && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="space-y-1.5"><Label>Deadline</Label><Input type="datetime-local" value={f.registration_deadline} onChange={(e) => set("registration_deadline", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Entry fee</Label><Input value={f.entry_fee} onChange={(e) => set("entry_fee", e.target.value)} placeholder="Free" /></div>
              <div className="space-y-1.5"><Label>Max participants</Label><Input type="number" value={f.max_participants} onChange={(e) => set("max_participants", e.target.value)} placeholder="0 = no limit" /></div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Prizes (optional)</Label><Input value={f.prizes} onChange={(e) => set("prizes", e.target.value)} placeholder="e.g. Trophy + ₹10,000" /></div>
            <div className="space-y-1.5"><Label>Volunteers needed</Label><Input type="number" value={f.volunteers_needed} onChange={(e) => set("volunteers_needed", e.target.value)} placeholder="0" /></div>
          </div>

          <div className="space-y-1.5"><Label>Cover image URL</Label><Input value={f.cover} onChange={(e) => set("cover", e.target.value)} placeholder="https://…" /></div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Video links (one per line)</Label><Textarea rows={2} value={f.video_urls} onChange={(e) => set("video_urls", e.target.value)} placeholder="https://youtube.com/…" /></div>
            <div className="space-y-1.5"><Label>Google Photos album URL</Label><Input value={f.google_album_url} onChange={(e) => set("google_album_url", e.target.value)} placeholder="https://photos.app.goo.gl/…" /></div>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} className="rounded-full" data-testid="ev-submit">Publish event</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FeedCard({ i, user, onImport }) {
  const d = new Date(i.published);
  return (
    <div className="group flex flex-col rounded-2xl border border-border/60 bg-card p-4 shadow-sm transition-colors hover:border-secondary/40" data-testid="event-feed-card">
      <a href={i.url} target="_blank" rel="noreferrer" className="flex gap-4">
        {i.image_url ? <img src={i.image_url} alt="" className="h-14 w-14 shrink-0 rounded-lg object-cover" onError={(e) => { e.target.style.display = "none"; }} />
          : <span className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-accent text-primary"><GlobeHemisphereWest size={22} weight="duotone" /></span>}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            {(i.tags || []).slice(0, 1).map((t) => <Badge key={t} variant="secondary" className="rounded-full font-normal">{t}</Badge>)}
            {i.country && <span className="text-xs text-muted-foreground">{i.country}</span>}
          </div>
          <h3 className="mt-1 line-clamp-2 text-sm font-medium leading-snug group-hover:text-secondary">{i.title}</h3>
          <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
            <span className="truncate">{i.source}</span>
            {!isNaN(d) && <span>· {d.toLocaleDateString("en", { day: "numeric", month: "short", year: "numeric" })}</span>}
          </div>
        </div>
      </a>
      <div className="mt-3 flex items-center gap-2 border-t border-border/50 pt-3">
        <a href={i.url} target="_blank" rel="noreferrer" className="text-xs font-medium text-muted-foreground hover:text-secondary"><ArrowSquareOut size={13} className="mr-1 inline" />Source</a>
        {user && (
          <Button onClick={() => onImport(i)} size="sm" variant="secondary" className="ml-auto rounded-full gap-1.5 text-xs" data-testid={`import-feed-${i.id}`}>
            <Plus size={13} /> Host this event
          </Button>
        )}
      </div>
    </div>
  );
}

export default function PublicEvents() {
  useSeo("Events", "Discover, promote and manage educational events, reunions and festivals across the world.");
  const { user } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("community");
  const [events, setEvents] = useState(null);
  const [feed, setFeed] = useState(null);
  const [facets, setFacets] = useState({ countries: [], types: [] });
  const [filters, setFilters] = useState({ country: "", event_type: "", mode: "", when: "upcoming", q: "" });

  const load = () => {
    const params = { when: filters.when };
    if (filters.country) params.country = filters.country;
    if (filters.event_type) params.event_type = filters.event_type;
    if (filters.mode) params.mode = filters.mode;
    if (filters.q) params.q = filters.q;
    api.get("/public/events", { params }).then((r) => setEvents(r.data)).catch(() => setEvents([]));
  };
  const loadFeed = () => {
    setFeed(null);
    const params = {};
    if (filters.country) params.country = filters.country;
    if (filters.q) params.q = filters.q;
    api.get("/public/events/feed", { params }).then((r) => setFeed(r.data.items || [])).catch(() => setFeed([]));
  };
  useEffect(() => { api.get("/public/events/facets").then((r) => setFacets(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (view === "community") load(); else loadFeed();
    /* eslint-disable-next-line */
  }, [filters, view]);
  const setF = (k, v) => setFilters((p) => ({ ...p, [k]: v === "all" ? "" : v }));

  const importEvent = async (item) => {
    try {
      const r = await api.post("/events/import-feed", { feed_id: item.id });
      toast.success("Event created — you can edit the details anytime");
      navigate(`/events/${r.data.id}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="mx-auto max-w-6xl px-5 py-14">
      <SampleLinks className="mb-6" />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="max-w-2xl">
          <p className="overline text-secondary mb-3">Gatherings, festivals & reunions</p>
          <h1 className="font-heading text-4xl font-light tracking-tight">Events - Reunions</h1>
          <p className="mt-3 text-muted-foreground">Create, promote and relive events from schools, colleges, universities, alumni associations, clubs and student organizations worldwide.</p>
        </div>
        <CreateEventDialog onCreated={load} />
      </div>

      <PageIntro
        title="Events - Reunions"
        tagline="Create, connect, celebrate, and relive the moments that bring communities together."
        intro={[
          `${EDITION.brand} makes it easy for schools, colleges, universities, alumni associations, clubs, student organizations, classes, and individual members to create, manage, promote, and preserve events from anywhere in the world.`,
          "From class reunions and alumni gatherings to campus celebrations, lectures, networking events, fundraisers, cultural programs, and student activities — keep everything organized in one place.",
        ]}
        sections={[
          { heading: "Create & Manage Events", body: "Create an event with its date, time, location, description, images, organizers, registration details, and virtual meeting links. Manage updates, attendees, guest lists, RSVPs, and communications as your event comes together." },
          { heading: "Promote Your Event", body: `Share events with your class, batch, institution, alumni community, club, organization, or the wider ${EDITION.brand} network. Make it easy for people to discover an event, learn more, and join.` },
          { heading: "Reunions Made Simple", body: "Planning a reunion? Create a dedicated event, invite classmates, track RSVPs, coordinate details, and keep everyone informed before, during, and after the gathering." },
          { heading: "Relive the Memories", body: "The event doesn't have to end when everyone goes home. Share photos, videos, messages, highlights, and memories so future generations of alumni can discover and experience the story." },
        ]}
        closing={`From the first invitation to the final photograph — ${EDITION.brand} helps you bring people together and preserve the moments that matter.`}
        kicker="Create an event. Bring your community together. Make memories that last."
      />

      <div className="mt-8 inline-flex rounded-full border border-border/60 bg-muted/40 p-1" data-testid="events-view-toggle">
        <button onClick={() => setView("community")} className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${view === "community" ? "bg-card text-primary shadow-sm" : "text-muted-foreground"}`} data-testid="view-community">Community events</button>
        <button onClick={() => setView("web")} className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${view === "web" ? "bg-card text-primary shadow-sm" : "text-muted-foreground"}`} data-testid="view-web">From around the web</button>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Input placeholder="Search events…" value={filters.q} onChange={(e) => setF("q", e.target.value)} className="max-w-xs rounded-full" data-testid="ev-search" />
        <Select value={filters.country || "all"} onValueChange={(v) => setF("country", v)}>
          <SelectTrigger className="w-40 rounded-full" data-testid="ev-filter-country"><SelectValue placeholder="Country" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All countries</SelectItem>{[...new Set([...facets.countries, "India", "United States", "United Kingdom", "Australia", "Canada", "Singapore"])].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        {view === "community" && <>
          <Select value={filters.event_type || "all"} onValueChange={(v) => setF("event_type", v)}>
            <SelectTrigger className="w-44 rounded-full" data-testid="ev-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent className="max-h-72"><SelectItem value="all">All types</SelectItem>{facets.types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.mode || "all"} onValueChange={(v) => setF("mode", v)}>
            <SelectTrigger className="w-36 rounded-full" data-testid="ev-filter-mode"><SelectValue placeholder="Mode" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Any mode</SelectItem>{["In-Person", "Online", "Hybrid"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.when} onValueChange={(v) => setF("when", v)}>
            <SelectTrigger className="w-36 rounded-full" data-testid="ev-filter-when"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="upcoming">Upcoming</SelectItem><SelectItem value="past">Past</SelectItem><SelectItem value="all">All</SelectItem></SelectContent>
          </Select>
        </>}
      </div>

      {view === "web" ? (
        feed === null ? <p className="mt-10 text-muted-foreground">Loading events from around the web…</p>
          : feed.length === 0 ? (
            <div className="mt-16 grid place-items-center rounded-2xl border border-dashed border-border/70 py-20 text-center">
              <GlobeHemisphereWest size={40} weight="duotone" className="text-muted-foreground" />
              <p className="mt-3 text-muted-foreground">No web events found{filters.country ? ` for ${filters.country}` : ""} yet. Try another country.</p>
            </div>
          ) : (
            <div className="mt-6">
              <p className="mb-4 text-sm text-muted-foreground">Educational events, festivals and reunions gathered from across the web{filters.country ? ` in ${filters.country}` : ""}. Links open the original source.</p>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="events-feed-grid">
                {feed.map((i) => <FeedCard key={i.id} i={i} user={user} onImport={importEvent} />)}
              </div>
            </div>
          )
      ) : events === null ? <p className="mt-10 text-muted-foreground">Loading events…</p>
        : events.length === 0 ? (
          <div className="mt-16 grid place-items-center rounded-2xl border border-dashed border-border/70 py-20 text-center">
            <CalendarStar size={40} weight="duotone" className="text-muted-foreground" />
            <p className="mt-3 text-muted-foreground">No events match your filters yet. Be the first to host one!</p>
          </div>
        ) : (
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="events-grid">
            {events.map((e) => <EventCard key={e.id} e={e} />)}
          </div>
        )}
    </div>
  );
}
